'use client';

import { CacheKey } from '@eliadmin/core/src/api/constants';
import { fetchOffers } from '@eliadmin/core/src/api/jobportal/offers-api';
import { JobOfferCard } from '@eliadmin/core/src/components/jobportal/offers/job-offer-card';
import { Button } from '@eliadmin/core/src/components/ui/button';
import { Pagination } from '@eliadmin/core/src/components/ui/pagination';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';
import {
  buildJobOffersQuery,
  useJobportalOffersStore,
} from '@eliadmin/core/src/stores/jobportal-offers-store';
import { useCustomerStore } from '@eliadmin/core/src/stores/customer-store';
import { useGlobalNavigation } from '@eliadmin/core/src/stores/global-store';
import { useLocaleStore } from '@eliadmin/core/src/stores/locale-store';
import { cn } from '@eliadmin/core/src/utils/style-utils';
import { useQueryClient, useSuspenseInfiniteQuery } from '@tanstack/react-query';
import { useSearchParams } from 'next/navigation';
import { useMemo } from 'react';
import { useDebounce } from '@eliadmin/core/src/hooks/use-debounce';
import { Spinner } from '@eliadmin/core/src/components/ui/spinner';

export type JobPortalOffersStoreProps = {
  showPagination?: boolean;
  showLoadMore?: boolean;
  paginationKey?: string;
  className?: string;
};

export function JobPortalOffersStore(props: JobPortalOffersStoreProps) {
  const locale = useLocaleStore(s => s.currentLocale);
  const authToken = useCustomerStore(s => s.me?.authToken);
  const searchParams = useSearchParams();
  const queryClient = useQueryClient();
  const state = useJobportalOffersStore();

  const currentPage = searchParams.get(props.paginationKey || 'page');

  const { t } = useTranslations();

  const baseQuery = useMemo(() => {
    const searchValue = state.search?.length && state.search.length > 2 ? state.search : undefined;
    return {
      ...buildJobOffersQuery({
        ...(state.filter || {}),
        status: 'live',
        is_paid: true,
        expires_at: true,
      } as any),
      'order[]': ['asc', state.order],
      'orderBy[]': ['topped_up_to', state.orderBy],
      search: searchValue,
      results: 30,
    } as any;
  }, [state.filter, state.order, state.orderBy, state.search]);

  const debouncedBaseQuery = useDebounce(baseQuery, 1500);

  const jobOffersQuery = useSuspenseInfiniteQuery({
    queryKey: [CacheKey.JOBPPORTAL_OFFERS, debouncedBaseQuery, currentPage, authToken, locale],
    queryFn: ({ pageParam }) => {
      return fetchOffers({
        query: {
          ...debouncedBaseQuery,
          page: pageParam,
          'order[]': ['desc'],
          'orderBy[]': ['topped_up_to'],
        } as any,
        locale,
        authToken,
      });
    },
    getNextPageParam: lastPage => {
      if (lastPage?.meta?.current_page < lastPage?.meta?.last_page)
        return lastPage?.meta?.current_page + 1;
      return undefined;
    },
    initialPageParam: currentPage ? parseInt(currentPage) : 1,
  });

  const isGlobalNavigating = useGlobalNavigation();

  const lastPage = jobOffersQuery.data?.pages[jobOffersQuery.data?.pages.length - 1];
  const flatList = jobOffersQuery.data?.pages.flatMap(page => page.data) ?? [];

  return (
    <div className={cn('relative flex w-full flex-col gap-6', 'ea-job-portal-offers-container')}>
      {jobOffersQuery.isFetching && <Spinner className="mx-auto py-4" />}
      {!flatList?.length && !jobOffersQuery.isFetching && (
        <p className="text-center text-muted-foreground">{t('text554')}</p>
      )}
      <div
        className={cn(
          'grid w-full grid-cols-1 gap-4',
          'ea-job-portal-offers-container__grid',
          props.className,
        )}
      >
        {flatList?.map((offer, i) => (
          <JobOfferCard key={i} offer={offer} />
        ))}
      </div>

      {!!jobOffersQuery.hasNextPage && props.showLoadMore && (
        <div
          className={cn(
            'flex flex-row justify-center',
            'ea-job-portal-offers-container__load-more',
          )}
        >
          <Button
            variant="secondary"
            isLoading={jobOffersQuery.isFetchingNextPage}
            onClick={() => jobOffersQuery.fetchNextPage()}
          >
            {t('text338')}
          </Button>
        </div>
      )}

      {props.showPagination && (
        <div
          className={cn(
            'flex flex-row justify-center',
            'ea-job-portal-offers-container__pagination',
          )}
        >
          <Pagination
            links={lastPage?.meta.links}
            onPageClicked={page => {
              if (String(page) !== String(currentPage)) return;
              queryClient.resetQueries({
                queryKey: [CacheKey.PRODUCTS, 'infinite', String(currentPage)],
                exact: true,
              });
            }}
          />
        </div>
      )}

      {!!isGlobalNavigating && (
        <div
          className={cn(
            'absolute inset-0 z-10 bg-background/30',
            'ea-category-products__loading-overlay',
          )}
        ></div>
      )}
    </div>
  );
}

export function JobPortalOffersStoreSkeleton() {
  return (
    <div
      className={cn('relative flex flex-col gap-6 @container', 'ea-job-portal-offers-container')}
    >
      <div className="flex w-full justify-center py-10">
        <Spinner className="h-8 w-8" />
      </div>
    </div>
  );
}

export {};
